import * as $ from 'jquery';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { EventHandleSpec } from './EventHandleSpec';
import { MiscUtil } from './utility/MiscUtil';
import { TableauEventHandlerForReact } from './TableauEventHandlerForReact';

// ------------------------------------
// Helper functions
interface EventForwarderProps extends React.HTMLProps<HTMLElement> {
  TagName: string;
  HtmlAttributes: React.HTMLProps<HTMLElement>;
  Spec: EventHandleSpec;
}
// ------------------------------------

/**
* A component that handles Tableau Events by forwarding raw React events to TableauEventHandler.
* Wrap your components under this to provide hooks to Tableau Events.
*/
class EventForwarder extends React.Component<EventForwarderProps, Object> {
  private eventHandler: TableauEventHandlerForReact = null;

  // EventForwarder maintains a reference to the current element and spec objects for equality comparison
  // when updates to the component occur. The should be used for no other purpose.
  private element: HTMLElement = null;
  private spec: EventHandleSpec = null;
  public constructor(props: EventForwarderProps) {
    super(props, null);
    this.eventHandler = TableauEventHandlerForReact.CreateEmpty();
  }

  /**
  * Shorthand for creating an EventForward component.
  */
  private static createElement(
    tagName: string,
    spec: EventHandleSpec,
    attrs: React.HTMLProps<HTMLElement>,
    ...children: Object[])
    : React.ReactElement<EventForwarderProps> {
    let evtFwdrProps: EventForwarderProps = {
      TagName: tagName,
      HtmlAttributes: attrs,
      Spec: spec,
      key: attrs.key
    };

    // move ref to the wrapper level so lookup through ref on event forwarder wrapper object works.
    if (!MiscUtil.IsNullOrUndefined(attrs.ref)) {
      evtFwdrProps.ref = attrs.ref;
      delete attrs.ref;
    }
    if (!MiscUtil.IsNullOrUndefined(children) && children.length === 1) {
      // TFSID 479359 if only one child, don't pass an array so React doesn't complain about it needing a key
      return React.createElement(EventForwarder, evtFwdrProps, children[0]);
    }
    return React.createElement(EventForwarder, evtFwdrProps, children);
  }

  public static div(spec: EventHandleSpec, attrs: React.HTMLProps<HTMLElement>, ...children: Object[])
    : React.ReactElement<EventForwarderProps> {
    return EventForwarder.createElement('div', spec, attrs, children);
  }

  public static span(spec: EventHandleSpec, attrs: React.HTMLProps<HTMLElement>, ...children: Object[])
    : React.ReactElement<EventForwarderProps> {
    return EventForwarder.createElement('span', spec, attrs, children);
  }

  public render(): JSX.Element {
    return React.createElement(this.props.TagName,
      // tslint:disable-next-line:align
      $.extend({}, this.eventHandler.EventHandlingAttributesForReact, this.props.HtmlAttributes), this.props.children);
  }

  public componentDidMount(): void {
    this.UpdateElementSpec();
  }

  public componentDidUpdate(): void {
    this.UpdateElementSpec();
  }

  public componentWillUnmount(): void {
    if (!MiscUtil.IsNullOrUndefined(this.eventHandler)) {
      this.eventHandler.dispose();
    }
  }

  private UpdateElementSpec(): void {
    if (!MiscUtil.IsNullOrUndefined(this.props.Spec)) {
      let newElement = ReactDOM.findDOMNode(this);

      // If either the DOM element associated with this React component OR the spec object has changed,
      // the eventHandler needs to be updated so it is working with the new objects in play. Without this
      // update to the eventHandler, this EventForwarder may stop working since it may still be listening
      // to events on a discarded DOM element or it may be triggering calls to handlers on an old spec.
      if (newElement instanceof HTMLElement) {
        if (this.element !== newElement || EventForwarder.HasEventHandleSpecChanged(this.spec, this.props.Spec)) {
          this.eventHandler.UpdateEventHandleSpec(newElement, this.props.Spec);
          this.element = newElement;
          this.spec = this.props.Spec;
        }
      }
    }
  }

  /**
  * Returns whether the event handle spec for the react element has changed. Make sure all event handlers
  * defined on the new spec are defined in the existing spec and they are structurally equal.
  * @param {EventHandleSpec} curSpec - current spec from
  * @param {EventHandleSpec} newSpec- new event spec from the props
  * @returns {boolean} if the spec is listening to new events
  */
  private static HasEventHandleSpecChanged(curSpec: EventHandleSpec, newSpec: EventHandleSpec): boolean {
    // this check is to prevent reloading the spec when it hasn't changed meaningfully,
    // so that single click events that casue the component to update do not prevent double-click events from being recognized
    // (TFSID: 404282, TFSID:415028, TFSID:423538)
    // Right now, it only recognizes adding event handlers as changes that require updates - in future, we might want to expand that
    return !Object.keys(newSpec).every(key => Object.keys(curSpec).indexOf(key) !== -1);
  }
}

export { EventForwarderProps, EventForwarder };
